import { NgIf, AsyncPipe } from '@angular/common';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';

import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatSlideToggleChange, MatSlideToggle } from '@angular/material/slide-toggle';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { CoreSelectors } from '@next/core-lib';
import { RootState } from '@next/core-lib/app';
import { BrandingService } from '@next/core-lib/branding';
import { PageToolbarComponent } from '@next/core-lib/components';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { TableColumn, TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { WrapperSiteSidenavActions } from '@next/core-lib/wrapper-site-sidenav';

import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { take, map, tap, takeUntil } from 'rxjs/operators';
import { FinalSetupNavigationService } from 'src/app/cycle/services/final-setup-navigation.service';

import { Delivery, DeliveryDataRow } from '../../models/delivery.model';

import {
  PostManualDeliveryRequest,
  ShowInFinalSetupDeliveryRequest,
} from '../../models/flock-registration-service.model';
import {
  upsertActiveDeliveriesRequest,
  removeManualDeliveryRequest,
  upsertManualDeliveryRequest,
  upsertShowInFinalSetupDeliveriesRequest,
} from '../../store/actions/delivery.actions';
import { getDeliveries, getActiveDelivery } from '../../store/selectors/delivery.selectors';
import { DeliveryOverviewFilterComponent } from '../delivery-overview-filter/delivery-overview-filter.component';

@Component({
  selector: 'app-delivery-overview',
  templateUrl: 'delivery-overview.component.html',
  styleUrls: ['delivery-overview.component.scss'],
  imports: [
    PageToolbarComponent,
    DeliveryOverviewFilterComponent,
    TableComponent,
    MatSlideToggle,
    NgIf,
    MatIconButton,
    MatIcon,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class DeliveryOverviewComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);
  private readonly dialogService = inject(DialogService);
  private readonly locale = inject(LocaleService);
  private readonly finalSetupNavigationService = inject(FinalSetupNavigationService);
  private readonly brandingService = inject(BrandingService);

  private readonly destroyed$ = new Subject<void>();
  deliveries$: Observable<DeliveryDataRow[]> = this.store.select(getDeliveries).pipe(
    map((deliveries: Delivery[]) => {
      const deliveryRows: DeliveryDataRow[] = [];
      deliveries.forEach((delivery) => {
        const rows = delivery.deliveryLines.map((deliveryLine) => ({
          deliveryDate: delivery.deliveryDate,
          deliveryId: delivery.externalDeliveryId,
          invoiceDate: deliveryLine.invoiceDate,
          invoiceId: deliveryLine.externalInvoiceId,
          externalUniqueIdentifier: deliveryLine.externalUniqueIdentifier,
          product: `${
            deliveryLine.externalProductId ? deliveryLine.externalProductId + ' - ' : ''
          } ${deliveryLine.productTitle} (${deliveryLine.unitsRemaining} KG ${this.locale.translate(
            'delivery-overview.remaining-of',
          )} ${deliveryLine.baseQuantity} KG )`,
          isActive: deliveryLine.isActive,
          data: delivery,
          showInFinalSetup: deliveryLine.showInFinalSetup,
        }));
        deliveryRows.push(...rows);
      });
      return deliveryRows;
    }),
  );
  isLoading$: Observable<boolean> = this.store.select(CoreSelectors.selectLoading);
  isMobile$ = this.breakpoint.ltLg$;
  isFinalSetup$: Observable<boolean> = this.finalSetupNavigationService.canNavigate();
  isFinalSetup: boolean;
  isAgrifirm: boolean = this.brandingService.isTenant('Agrifirm');

  tableConfig: TableConfig = {
    columns: [],
    dataSource: new MatTableDataSource<DeliveryDataRow>([]),
    selectColumn: false,
    actionColumn: false,
    scrolling: true,
    disableCards: true,
    wrapperWidth: '100%',
    activeSort: {
      id: 'deliveryDate',
      start: 'desc',
      disableClear: false,
    },
  };

  @ViewChild('isActiveColumn') isActiveColumn: TemplateRef<void>;
  @ViewChild('actionButtonsColumn') actionButtonsColumn: TemplateRef<void>;
  @ViewChild('showInFinalSetupColumn') showInFinalSetupColumn: TemplateRef<void>;

  columns = <TableColumn[]>[
    {
      id: 'deliveryDate',
      type: TableColumnTypes.DATE,
      label: this.locale.translate('delivery-overview.table.deliveryDate'),
    },
    {
      id: 'deliveryId',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('delivery-overview.table.deliveryId'),
    },
    {
      id: 'invoiceDate',
      type: TableColumnTypes.DATE,
      label: this.locale.translate('delivery-overview.table.invoiceDate'),
    },
    {
      id: 'invoiceId',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('delivery-overview.table.invoiceNumber'),
    },
    {
      id: 'product',
      className: 'product-column',
      type: TableColumnTypes.STRING,
      label: this.locale.translate('delivery-overview.table.product'),
    },
    {
      id: 'isActive',
      type: TableColumnTypes.TEMPLATE,
      label: this.locale.translate('delivery-overview.table.isActive'),
    },
    {
      id: 'actionButtons',
      type: TableColumnTypes.TEMPLATE,
    },
  ];

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {}

  ngOnInit() {
    this.isFinalSetup$.pipe(take(1)).subscribe((isFinalSetup) => {
      if (isFinalSetup) {
        this.columns.splice(6, 0, {
          id: 'showInFinalSetup',
          type: TableColumnTypes.TEMPLATE,
          label: this.locale.translate('delivery-overview.table.showInFinalSetup'),
        });
      }

      this.deliveries$
        .pipe(takeUntil(this.destroyed$))
        .subscribe((deliveries: DeliveryDataRow[]) => {
          this.tableConfig = {
            ...this.tableConfig,
            columns: this.columns,
            dataSource: new MatTableDataSource<DeliveryDataRow>(deliveries),
          };

          const newConfig = { ...this.tableConfig };
          const isActiveRef = newConfig.columns.find((c) => c.id === 'isActive');
          const actionButtonsRef = newConfig.columns.find((c) => c.id === 'actionButtons');
          const showInFinalSetupRef = newConfig.columns.find((c) => c.id === 'showInFinalSetup');

          if (isActiveRef) {
            isActiveRef.template = this.isActiveColumn;
          }
          if (actionButtonsRef) {
            actionButtonsRef.template = this.actionButtonsColumn;
          }
          if (showInFinalSetupRef) {
            showInFinalSetupRef.template = this.showInFinalSetupColumn;
          }
          this.tableConfig = newConfig;
        });
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  setActive(event: MatSlideToggleChange, delivery: Delivery, externalUniqueIdentifier: string) {
    this.store
      .select(getActiveDelivery)
      .pipe(
        map((f) => f(delivery.id)),
        take(1),
      )
      .subscribe((activeDelivery) => {
        if (
          delivery.deliveryLines.some(
            (line) =>
              line.externalUniqueIdentifier === externalUniqueIdentifier &&
              line.baseQuantity === line.unitsRemaining,
          )
        ) {
          activeDelivery = event.checked
            ? {
                ...activeDelivery,
                activeDeliveryLineExternalUniqueIdentifiers: [
                  ...activeDelivery.activeDeliveryLineExternalUniqueIdentifiers,
                  externalUniqueIdentifier,
                ],
                deliveryOrigin: delivery.deliveryOrigin,
              }
            : {
                ...activeDelivery,
                activeDeliveryLineExternalUniqueIdentifiers:
                  activeDelivery.activeDeliveryLineExternalUniqueIdentifiers.filter(
                    (l: string) => l !== externalUniqueIdentifier,
                  ),
                deliveryOrigin: delivery.deliveryOrigin,
              };
          this.store.dispatch(
            upsertActiveDeliveriesRequest({
              activeDelivery: {
                deliveryId: activeDelivery.deliveryId,
                activeDeliveryLineExternalUniqueIdentifiers:
                  activeDelivery.activeDeliveryLineExternalUniqueIdentifiers,
                origin: activeDelivery.deliveryOrigin,
              },
            }),
          );
        } else {
          this.dialogService.notify({
            title: this.locale.translate('delivery-overview.no-deactivation.title'),
            text: this.locale.translate('delivery-overview.no-deactivation.description'),
          });

          // switch the isActive toggle back on
          let rows = this.tableConfig.dataSource.data.map((row: DeliveryDataRow) => ({ ...row }));
          rows = [
            ...[...rows].filter((row) => row.deliveryId !== delivery.externalDeliveryId),
            {
              ...[...rows].find((row) => row.deliveryId === delivery.externalDeliveryId),
              isActive: true,
            },
          ];
          this.tableConfig.dataSource.data = rows;
        }
      });
  }

  canRemove(delivery: Delivery): boolean {
    return delivery.deliveryLines.filter((d) => d.baseQuantity !== d.unitsRemaining).length === 0;
  }

  add() {
    this.router.navigate(['/delivery', 'add']);
    this.breakpoint.ltLg$.pipe(take(1)).subscribe((isMobile) => {
      if (isMobile) {
        this.store.dispatch(WrapperSiteSidenavActions.closeSidenav());
      }
    });
  }

  editManualDelivery(manualDeliveryId: number) {
    this.router.navigate(['/delivery', manualDeliveryId]);
    this.breakpoint.ltLg$.pipe(take(1)).subscribe((isMobile) => {
      if (isMobile) {
        this.store.dispatch(WrapperSiteSidenavActions.closeSidenav());
      }
    });
  }

  deleteManualDelivery(manualDeliveryId: number) {
    this.dialogService
      .confirmWarning({
        title: this.locale.translate('delivery-overview.remove-delivery-warning.title'),
        text: this.locale.translate('delivery-overview.remove-delivery-warning.text'),
      })
      .pipe(
        tap((result) => {
          if (result.status === DIALOG_RETURN_TYPES.confirm) {
            this.store.dispatch(removeManualDeliveryRequest({ manualDeliveryId }));
          }
        }),
        take(1),
      )
      .subscribe();
  }

  onToggleShowInFinalSetup(
    event: MatSlideToggleChange,
    delivery: Delivery,
    externalUniqueIdentifier: string,
  ) {
    if (delivery.deliveryType === 'Manual') {
      // upsert manual delivery line
      const manualDeliveryRequest = {
        id: delivery.id,
        deliveryDate: delivery.deliveryDate,
        manualDeliveryLines: [
          {
            ...delivery.deliveryLines[0],
            showInFinalSetup: event.checked,
          },
        ],
      } as PostManualDeliveryRequest;

      this.store.dispatch(
        upsertManualDeliveryRequest({
          manualDeliveryRequest,
        }),
      );
    } else {
      // upsert automatic delivery line
      const showInFinalSetupDeliveryRequest = {
        deliveryId: delivery.id,
        showInFinalSetup: event.checked,
        deliveryLineExternalUniqueIdentifier: externalUniqueIdentifier,
      } as ShowInFinalSetupDeliveryRequest;

      this.store.dispatch(
        upsertShowInFinalSetupDeliveriesRequest({
          request: showInFinalSetupDeliveryRequest,
        }),
      );
    }
  }
}
